import {useEffect, useRef, useState} from 'react';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';

export interface WhiteboardContainerDimensions {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  x: number;
  y: number;
}

const initialDimensions: WhiteboardContainerDimensions = {
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: 0,
  height: 0,
  x: 0,
  y: 0,
};

function useWhiteboardContainerDimensions(): WhiteboardContainerDimensions {
  const [dimensions, setDimensions] = useState<WhiteboardContainerDimensions>(initialDimensions);
  const currentWhiteboardContainerRef = useRef<HTMLElement | null>(null);

  useEffect((): (() => void) => {
    let cleanup: (() => void) | undefined;
    let intervalId: number | undefined;
    let checkIntervalId: number | undefined;
    let resizeObserver: ResizeObserver | undefined;

    const setupObservers = (): boolean => {
      const whiteboardContainer = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER]?.get(0);

      if (!whiteboardContainer) {
        return false;
      }

      // If we're already observing the same element, don't re-setup
      if (currentWhiteboardContainerRef.current === whiteboardContainer && resizeObserver) {
        return true;
      }

      // Clean up old observers if element changed
      if (cleanup) {
        cleanup();
        cleanup = undefined;
      }

      // Update ref
      currentWhiteboardContainerRef.current = whiteboardContainer;

      const updateDimensions = (): void => {
        const rect = whiteboardContainer.getBoundingClientRect();
        setDimensions({
          top: rect.top,
          left: rect.left,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height,
          x: rect.x,
          y: rect.y,
        });
      };

      updateDimensions();

      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });

      resizeObserver.observe(whiteboardContainer);

      whiteboardContainer.addEventListener('scroll', updateDimensions);
      window.addEventListener('resize', updateDimensions);

      cleanup = (): void => {
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = undefined;
        }
        whiteboardContainer.removeEventListener('scroll', updateDimensions);
        window.removeEventListener('resize', updateDimensions);
        currentWhiteboardContainerRef.current = null;
      };

      return true;
    };

    // Try to set up immediately
    if (setupObservers()) {
      // Set up periodic check for element changes
      checkIntervalId = window.setInterval(() => {
        const whiteboardContainer = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER]?.get(0);
        if (whiteboardContainer && whiteboardContainer !== currentWhiteboardContainerRef.current) {
          // Element changed, re-setup
          setupObservers();
        }
      }, 100);

      return (): void => {
        if (checkIntervalId !== undefined) {
          window.clearInterval(checkIntervalId);
        }
        cleanup?.();
      };
    }

    // If not available, poll until it is
    intervalId = window.setInterval(() => {
      if (setupObservers()) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }

        // Set up periodic check for element changes
        checkIntervalId = window.setInterval(() => {
          const whiteboardContainer = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER]?.get(0);
          if (whiteboardContainer && whiteboardContainer !== currentWhiteboardContainerRef.current) {
            // Element changed, re-setup
            setupObservers();
          }
        }, 100);
      }
    }, 50);

    return (): void => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      if (checkIntervalId !== undefined) {
        window.clearInterval(checkIntervalId);
      }
      cleanup?.();
    };
  }, []);

  return dimensions;
}

export default useWhiteboardContainerDimensions;

